import { FeConstants } from 'constants/FeConstants';

const ZOHO_STATE_KEY = 'zoho_oauth_state';
const ZOHO_START_PATH = '/connect/zoho/start';

const generateState = (): string => {
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

/**
 * Mints a `state` nonce, keeps it for the callback route to check, and returns
 * the Zoho authorization URL carrying it. sessionStorage is per-origin, so this
 * must run on the origin Zoho redirects back to — see `getZohoConnectUrl`.
 */
export const buildZohoLoginUrl = (): string => {
  const state = generateState();
  sessionStorage.setItem(ZOHO_STATE_KEY, state);
  return FeConstants.ZOHO_LOGIN_URL(state);
};

/**
 * Where a "Connect Zoho" click sends the browser. A front end served from a
 * different origin than the registered callback (local development, which
 * calls back to the dev host) first hops to that origin's start route, so the
 * nonce is minted where the callback can read it. Local and dev share a back
 * end and its session cookie, so the connection made there is the one local
 * Verify reads back.
 */
export const getZohoConnectUrl = (): string => {
  const callbackOrigin = new URL(FeConstants.ZOHO_REDIRECT_URI).origin;
  if (callbackOrigin !== window.location.origin) {
    return `${callbackOrigin}${ZOHO_START_PATH}`;
  }
  return buildZohoLoginUrl();
};

export const isZohoOauthStateValid = (state: unknown): boolean =>
  typeof state === 'string' &&
  state !== '' &&
  state === sessionStorage.getItem(ZOHO_STATE_KEY);

export const clearZohoOauthState = (): void =>
  sessionStorage.removeItem(ZOHO_STATE_KEY);
